/**
 * Search and route planning across the galaxy.
 *
 * Two jobs that share one spatial index:
 *
 *   * **search** — find the nearest systems matching some criterion, expanding
 *     outward from wherever you are.
 *   * **routing** — the fewest hyperjumps between two systems, where a jump can
 *     cross at most `JUMP_RANGE`.
 *
 * ── Why search expands outward ──────────────────────────────────────────────
 *
 * Matching on station pads or planet types needs a system's *contents*, and
 * contents cost far more to derive than the summaries the map draws. Generating
 * all twelve thousand to answer "where is the nearest shipyard" would be
 * absurd when the answer is almost always a few hundred units away. So
 * candidates are visited nearest-first and generation stops as soon as enough
 * matches are found.
 */
import { getGalaxyMap, getSystem, } from './generate.js';
import { sizeRank, } from './types.js';
/**
 * Travel comes in two kinds, and they are deliberately not the same mechanic.
 *
 * **Jumps** move between systems inside one cluster and need only an engine.
 * Range depends on the engine, so a better drive covers the same cluster in
 * fewer hops — that is the whole payoff of upgrading it.
 *
 * **Cluster warps** cross between clusters, consume a warp core, and can only
 * be started from the system in the current cluster nearest the destination
 * cluster. Reaching another cluster is meant to be an event, not a longer hop.
 *
 * Jumps are confined to the current cluster *by rule*, not by distance. The
 * measured cross-cluster gap runs from 3,367 at its narrowest to a median of
 * 12,052, so a merely generous engine would let some cluster pairs be hopped
 * for free while others stayed impossible — arbitrary, and it would undercut
 * warp cores entirely. Restricting by cluster keeps the two modes distinct
 * however far engines are eventually upgraded.
 */
/**
 * World units to one galactic unit.
 *
 * Two coordinate systems meet here and they were being read as one. Inside a
 * system everything is world units — a station deck is 90 across, a planet
 * orbits at 44,000. Between systems everything is galactic units, and a
 * neighbour sits 1,000 to 4,000 of those away. Printed side by side that said
 * the next star was nearer than the local gas giant, which is not a rounding
 * error, it is a different scale being shown in the same unit.
 *
 * A thousand to one puts them in the right order: a neighbouring system reads
 * as one to four *million* units, which is comfortably past everything in the
 * system you are standing in and makes a jump the only sane way to cover it.
 *
 * Only ever applied when a galactic distance is *shown*. Ranges, gateways and
 * routing all stay in galactic units, because converting them would be
 * multiplying both sides of every comparison by the same number.
 */
export const WORLD_UNITS_PER_GALACTIC = 1000;
export const JUMP_RANGES = {
    /** Enough to connect every cluster internally; the worst gap measured is 4,196. */
    basic: 4200,
    improved: 6800,
    advanced: 10500,
};
export const JUMP_RANGE = JUMP_RANGES.basic;
/**
 * How far a starter hull can cross *between* clusters, in galactic units.
 *
 * Here rather than with the ship rules, alongside `JUMP_RANGES`, because it is
 * a fact about the galaxy's shape before it is a fact about any hull — and
 * because putting it there and importing it back would make this module and
 * `ship/jump` import each other, which is a cycle with a module-scope constant
 * inside it and therefore a crash waiting for the wrong load order.
 *
 * Measured: the worst-placed cluster on the dev seed has its nearest neighbour
 * 18,285 away by gateway, so anything below that strands somebody with a core
 * they cannot spend. Twenty thousand clears it and leaves that cluster four
 * neighbours to choose between.
 */
export const CLUSTER_JUMP_BASE = 20_000;
/**
 * The most any drive can cross in one warp.
 *
 * A little over twice the starter's reach. The galaxy runs about 195,000 across
 * at its widest, so even the best engine needs five warps — and five cores — to
 * cross it, which is what keeps the core a decision rather than a formality.
 */
export const CLUSTER_JUMP_CEILING = 45_000;
function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}
/** Does this system satisfy the query, and what should the result say? */
function matchSystem(system, query) {
    if (query.kind === 'station') {
        const wanted = query.minPad ? sizeRank(query.minPad) : -1;
        for (const station of system.stations) {
            if (query.needsAdvancedFabricator && !station.hasAdvancedFabricator)
                continue;
            if (wanted >= 0 && !station.pads.some((p) => sizeRank(p.size) >= wanted))
                continue;
            return station.name;
        }
        return null;
    }
    if (query.kind === 'planet') {
        const planet = query.planetType
            ? system.planets.find((p) => p.type === query.planetType)
            : system.planets[0];
        return planet ? `${planet.name} (${planet.type})` : null;
    }
    const field = query.field
        ? system.asteroidFields.find((f) => f.kind === query.field)
        : system.asteroidFields[0];
    return field ? field.name : null;
}
/**
 * Spatial index over every system, shared by search and routing.
 *
 * Built once per seed and reused — the index costs a full pass over the galaxy
 * summaries, which is cheap, but not something to repeat per keystroke.
 */
export class Navigator {
    #byId = new Map();
    /** Uniform grid, cell size = jump range, so neighbours are within 27 cells. */
    #grid = new Map();
    #seed;
    #gateways = new Map();
    #clusterCentres = new Map();
    /** The widest cluster there is, as slack on the centre-distance prefilter. */
    #clusterSpan = 0;
    /**
     * Which galaxy this index is of.
     *
     * Exposed because anything holding a `Navigator` is already working in one
     * seed's galaxy, and making them carry the string separately is how the two
     * end up disagreeing.
     */
    get seed() {
        return this.#seed;
    }
    constructor(seed, jumpRange = JUMP_RANGE) {
        this.#seed = seed;
        this.jumpRange = jumpRange;
        const map = getGalaxyMap(seed);
        this.systems = map.flatMap((entry) => entry.systems);
        // Centres and the widest radius, for the cheap half of `#warpNeighbours`.
        for (const { cluster } of map) {
            this.#clusterCentres.set(cluster.id, cluster.position);
            this.#clusterSpan = Math.max(this.#clusterSpan, cluster.radius * 2);
        }
        for (const system of this.systems) {
            this.#byId.set(system.id, system);
            const key = this.#cellKey(system.galacticPosition);
            const cell = this.#grid.get(key);
            if (cell)
                cell.push(system);
            else
                this.#grid.set(key, [system]);
        }
    }
    #cellKey(p) {
        const s = this.jumpRange;
        return `${Math.floor(p.x / s)},${Math.floor(p.y / s)},${Math.floor(p.z / s)}`;
    }
    get(id) {
        return this.#byId.get(id);
    }
    /**
     * Systems reachable in one jump.
     *
     * Same cluster only — see the JUMP_RANGES note. A longer-range engine covers
     * more of its own cluster per hop but never leaves it.
     */
    neighbours(id, jumpRange = this.jumpRange) {
        const from = this.#byId.get(id);
        if (!from)
            return [];
        // Cell size is fixed at construction, so a longer range has to search a
        // wider block of cells to stay correct.
        const span = Math.max(1, Math.ceil(jumpRange / this.jumpRange));
        const cx = Math.floor(from.galacticPosition.x / this.jumpRange);
        const cy = Math.floor(from.galacticPosition.y / this.jumpRange);
        const cz = Math.floor(from.galacticPosition.z / this.jumpRange);
        const found = [];
        for (let dx = -span; dx <= span; dx++) {
            for (let dy = -span; dy <= span; dy++) {
                for (let dz = -span; dz <= span; dz++) {
                    const cell = this.#grid.get(`${cx + dx},${cy + dy},${cz + dz}`);
                    if (!cell)
                        continue;
                    for (const candidate of cell) {
                        if (candidate.id === id)
                            continue;
                        if (candidate.clusterId !== from.clusterId)
                            continue;
                        if (distance(from.galacticPosition, candidate.galacticPosition) <= jumpRange) {
                            found.push(candidate);
                        }
                    }
                }
            }
        }
        return found;
    }
    /** Every system belonging to one cluster. */
    systemsIn(clusterId) {
        return this.systems.filter((system) => system.clusterId === clusterId);
    }
    /**
     * Where a warp between two clusters has to depart from and arrive at.
     *
     * The closest pair of systems across the two clusters — you cannot warp from
     * wherever you happen to be, you have to get to the edge nearest where you
     * are going first. That is what makes a cluster warp a piece of planning
     * rather than a button.
     */
    gateway(fromClusterId, toClusterId) {
        if (fromClusterId === toClusterId)
            return null;
        // Cached: a route across several clusters asks for the same pairs
        // repeatedly while searching, and each answer is every system in one
        // cluster against every system in the other.
        const key = `${fromClusterId}>${toClusterId}`;
        const known = this.#gateways.get(key);
        if (known !== undefined)
            return known;
        const origin = this.systemsIn(fromClusterId);
        const destination = this.systemsIn(toClusterId);
        if (origin.length === 0 || destination.length === 0)
            return null;
        let best = null;
        for (const a of origin) {
            for (const b of destination) {
                const gap = distance(a.galacticPosition, b.galacticPosition);
                if (!best || gap < best.distance)
                    best = { from: a.id, to: b.id, distance: gap };
            }
        }
        this.#gateways.set(key, best);
        return best;
    }
    /**
     * Clusters reachable from this one in a single warp.
     *
     * Filtered on cluster *centres* first, which is a cheap and generous test —
     * a gateway is measured between systems and is therefore always shorter than
     * the gap between the middles they sit in. The expensive per-pair check then
     * runs only on what survives.
     *
     * ── Public, and that is the point ───────────────────────────────────────
     *
     * The route planner and the cockpit's jump module both have to answer "which
     * clusters can I reach", and when they answered it separately they disagreed:
     * the module looked at the nearest ten clusters by centre while the planner
     * used the drive's actual reach, which at the top of the range covers forty.
     * A player was told to cross to somewhere no marker was ever drawn for. They
     * cannot drift apart if it is one function.
     */
    warpNeighbours(clusterId, clusterRange) {
        const home = this.#clusterCentres.get(clusterId);
        if (!home)
            return [];
        const found = [];
        for (const [id, centre] of this.#clusterCentres) {
            if (id === clusterId)
                continue;
            if (distance(home, centre) > clusterRange + this.#clusterSpan)
                continue;
            const gate = this.gateway(clusterId, id);
            if (gate && gate.distance <= clusterRange)
                found.push({ id, gate });
        }
        return found;
    }
    /**
     * The fewest warps from one cluster to another.
     *
     * Breadth-first over clusters, exactly as `route` is over systems, and for
     * the same reason: every warp costs one core whatever its length, so the
     * question is how many rather than how far.
     */
    #warpChain(fromClusterId, toClusterId, clusterRange) {
        if (fromClusterId === toClusterId)
            return [];
        const cameFrom = new Map();
        const seen = new Set([fromClusterId]);
        let frontier = [fromClusterId];
        // Bounded so an unreachable destination costs a known amount rather than
        // walking all seven hundred clusters to prove a negative.
        for (let depth = 0; depth < 12 && frontier.length > 0; depth++) {
            const next = [];
            for (const current of frontier) {
                for (const { id, gate } of this.warpNeighbours(current, clusterRange)) {
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    cameFrom.set(id, { previous: current, gate });
                    if (id === toClusterId) {
                        const chain = [];
                        let at = id;
                        while (at !== fromClusterId) {
                            const step = cameFrom.get(at);
                            chain.unshift(step.gate);
                            at = step.previous;
                        }
                        return chain;
                    }
                    next.push(id);
                }
            }
            frontier = next;
        }
        return null;
    }
    /**
     * Fewest jumps from one system to another.
     *
     * Breadth-first, because every jump costs the same regardless of length —
     * the question is "how many hops", not "how far". Weighting by distance would
     * need Dijkstra and would answer a question nobody asked.
     */
    route(fromId, toId, jumpRange = this.jumpRange) {
        if (!this.#byId.has(fromId) || !this.#byId.has(toId))
            return null;
        if (fromId === toId)
            return { path: [fromId], jumps: 0, distance: 0 };
        const cameFrom = new Map();
        const seen = new Set([fromId]);
        let frontier = [fromId];
        while (frontier.length > 0) {
            const next = [];
            for (const current of frontier) {
                for (const neighbour of this.neighbours(current, jumpRange)) {
                    if (seen.has(neighbour.id))
                        continue;
                    seen.add(neighbour.id);
                    cameFrom.set(neighbour.id, current);
                    if (neighbour.id === toId)
                        return this.#buildRoute(cameFrom, fromId, toId);
                    next.push(neighbour.id);
                }
            }
            frontier = next;
        }
        // Unreachable: the destination is beyond any chain of jumps from here.
        return null;
    }
    #buildRoute(cameFrom, fromId, toId) {
        const path = [toId];
        let cursor = toId;
        while (cursor !== fromId) {
            const previous = cameFrom.get(cursor);
            if (!previous)
                break;
            path.push(previous);
            cursor = previous;
        }
        path.reverse();
        let travelled = 0;
        for (let i = 1; i < path.length; i++) {
            const a = this.#byId.get(path[i - 1]);
            const b = this.#byId.get(path[i]);
            if (a && b)
                travelled += distance(a.galacticPosition, b.galacticPosition);
        }
        return { path, jumps: path.length - 1, distance: travelled };
    }
    /**
     * A complete journey, across clusters if need be.
     *
     * Same cluster is a single jump leg. Otherwise it is three: jump to the
     * gateway system, warp across, then jump onward to the destination — and the
     * warp is what costs a core.
     */
    planRoute(fromId, toId, jumpRange = this.jumpRange, clusterRange = CLUSTER_JUMP_BASE) {
        const from = this.#byId.get(fromId);
        const to = this.#byId.get(toId);
        if (!from || !to)
            return null;
        if (from.clusterId === to.clusterId) {
            const leg = this.route(fromId, toId, jumpRange);
            if (!leg)
                return null;
            return {
                legs: [{ kind: 'jump', path: leg.path, jumps: leg.jumps, distance: leg.distance }],
                jumps: leg.jumps,
                warps: 0,
                distance: leg.distance,
                path: leg.path,
            };
        }
        /*
         * One warp per cluster boundary, chained.
         *
         * This used to warp straight from one cluster to the other however far
         * apart they were, which put no limit on a crossing at all: one core bought
         * a hop to the far side of the galaxy, and the jump module — which only
         * ever drew nearby clusters — would never offer the leg the plan named. Now
         * a crossing has a reach like any other jump, and getting further means
         * more of them, each costing its own core.
         */
        const chain = this.#warpChain(from.clusterId, to.clusterId, clusterRange);
        if (!chain || chain.length === 0)
            return null;
        const legs = [];
        const path = [];
        let jumps = 0;
        let travelled = 0;
        let at = fromId;
        for (const gate of chain) {
            // Fly to the departure system, warp across, and carry on from where it
            // put us. A gateway is a *pair*, so arriving is already inside the next
            // cluster and the following leg starts there.
            const approach = this.route(at, gate.from, jumpRange);
            if (!approach)
                return null;
            if (approach.jumps > 0) {
                legs.push({
                    kind: 'jump',
                    path: approach.path,
                    jumps: approach.jumps,
                    distance: approach.distance,
                });
                jumps += approach.jumps;
                travelled += approach.distance;
            }
            legs.push({ kind: 'warp', from: gate.from, to: gate.to, distance: gate.distance });
            travelled += gate.distance;
            /*
             * The path counts every step, and a warp is one of them.
             *
             * Each approach begins where the previous warp landed, so its first
             * system is already on the path and is sliced off — except the very
             * first, which begins where the ship is. The warp then contributes
             * exactly one entry: the system it arrives at. That is what keeps
             * `jumps + warps === path.length - 1`, which several things rely on to
             * work out which leg a given step belongs to.
             */
            path.push(...(path.length === 0 ? approach.path : approach.path.slice(1)));
            path.push(gate.to);
            at = gate.to;
        }
        const onward = this.route(at, toId, jumpRange);
        if (!onward)
            return null;
        if (onward.jumps > 0) {
            legs.push({ kind: 'jump', path: onward.path, jumps: onward.jumps, distance: onward.distance });
            jumps += onward.jumps;
            travelled += onward.distance;
        }
        // Sliced for the same reason: the last warp already put its arrival on.
        path.push(...onward.path.slice(1));
        return { legs, jumps, warps: chain.length, distance: travelled, path };
    }
    /**
     * Nearest systems matching a query, closest first.
     *
     * `scanLimit` bounds how many systems may be fully generated before giving
     * up, so a query with no answer costs a known amount rather than the whole
     * galaxy.
     */
    search(originId, query, { limit = 10, scanLimit = 1500 } = {}) {
        const origin = this.#byId.get(originId);
        if (!origin)
            return [];
        // Order by distance first, using only summaries — cheap. Contents are
        // derived one system at a time as the list is walked.
        const candidates = this.systems
            .map((system) => ({
            system,
            distance: distance(origin.galacticPosition, system.galacticPosition),
        }))
            .sort((a, b) => a.distance - b.distance);
        const results = [];
        for (const candidate of candidates.slice(0, scanLimit)) {
            // Skip systems that cannot possibly match, using the summary alone. This
            // is what keeps the expensive generation off most candidates.
            if (query.kind === 'station' && candidate.system.stationCount === 0)
                continue;
            if (query.kind === 'planet' && candidate.system.planetCount === 0)
                continue;
            if (query.kind === 'asteroid' && candidate.system.asteroidFieldCount === 0)
                continue;
            const label = matchSystem(getSystem(this.#seed, candidate.system.id), query);
            if (!label)
                continue;
            results.push({ system: candidate.system, distance: candidate.distance, label });
            if (results.length >= limit)
                break;
        }
        return results;
    }
    /**
     * How much of the galaxy is reachable from one system.
     *
     * A jump range too short for the gaps between clusters silently strands
     * players, and nothing else in the game would report it.
     */
    connectivity(fromId = this.systems[0].id) {
        const seen = new Set([fromId]);
        let frontier = [fromId];
        while (frontier.length > 0) {
            const next = [];
            for (const current of frontier) {
                for (const neighbour of this.neighbours(current)) {
                    if (seen.has(neighbour.id))
                        continue;
                    seen.add(neighbour.id);
                    next.push(neighbour.id);
                }
            }
            frontier = next;
        }
        return { reachable: seen.size, total: this.systems.length };
    }
}
//# sourceMappingURL=navigation.js.map